<template>
    <RegularModal ref="modal" modal-card-class="is-large">
        <template v-slot:title>{{ translate("admin_competitors.copy_to_category") }}</template>
        <template v-slot:body>
            <div class="mb-3 is-size-5" v-if="registeredCompetitor">
                <p>
                    <span class="has-text-grey-light">{{ translate("page.start_fights.competitor") }}:</span>
                    <Country :country-code="registeredCompetitor.nationality" :only-flag="true" />
                    <span class="clarity-mask">{{ registeredCompetitor.firstName }} {{ registeredCompetitor.lastName }}</span>
                </p>
                <p>
                    <span class="has-text-grey-light">{{ translate("form.parameter_id") }}</span>
                    {{ registeredCompetitor.category }}
                </p>
            </div>

            <CategorySelector
                :current-parameter-id="registeredCompetitor?.parameterId"
                @selected="onCategorySelected"
                @selection-cleared="selectedCategory = null"
                ref="categorySelector"
                :event-id="eventId"
                :show-selected-category="true"
                :selected-category-warning="
                    selectedCategory?.key === registeredCompetitor?.parameterId
                        ? translate('admin_competitors.copy_current_category_warning')
                        : undefined
                "
            >
                <template #aside>
                    <CategoryRosterPreview :event-id="eventId" :parameter-id="selectedCategory?.key ?? null" />
                </template>
            </CategorySelector>
        </template>
        <template v-slot:footer>
            <div class="buttons">
                <LoadingButton :is-loading="copyingInProgress" class="button is-primary" @click="confirm" :disabled="!canCopy">
                    {{ translate("common.ok") }}
                </LoadingButton>
                <button class="button" @click="close">{{ translate("common.cancel") }}</button>
            </div>
        </template>
    </RegularModal>
</template>

<script setup lang="ts">
    import { ref, computed } from "vue";
    import RegularModal from "../Common/RegularModal.vue";
    import Country from "../Common/Country.vue";
    import CategorySelector from "./CategorySelector.vue";
    import CategoryRosterPreview from "./CategoryRosterPreview.vue";
    import LoadingButton from "../Common/LoadingButton.vue";
    import { translate } from "../Common/translations";
    import eventCompetitorsApi from "../Api/eventCompetitors";
    import { successToast, warningToast } from "../Common/toasts";
    import { handleError } from "../Common/errorHandler";
    import { CompetitorPaymentWithMetadata } from "../Api/payments";
    import { useRegisteredCompetitorsStore } from "./registeredCompetitorsStore";
    import { eventBus, OPEN_SIDE_EFFECTS_REPORT_MODAL } from "./eventBus";
    import { EventRegistrationSideEffectReport } from "../Common/types";

    const store = useRegisteredCompetitorsStore();

    const modal = ref();
    const categorySelector = ref<InstanceType<typeof CategorySelector> | null>(null);
    const registeredCompetitor = ref<CompetitorPaymentWithMetadata | null>(null);
    const selectedCategory = ref<any>(null);
    const copyingInProgress = ref(false);
    const initDone = ref(false);

    const eventId = computed(() => store.eventId);

    const canCopy = computed(() => !!selectedCategory.value?.key && selectedCategory.value.key !== registeredCompetitor.value?.parameterId);

    function onCategorySelected(category: any) {
        selectedCategory.value = category;
    }

    function open(competitor: any) {
        modal.value.open();
        registeredCompetitor.value = competitor;
        selectedCategory.value = null;
        initDone.value = false;

        categorySelector.value
            ?.init()
            .catch(handleError)
            .finally(() => {
                initDone.value = true;
            });
    }

    defineExpose({ open });

    function confirm() {
        if (!registeredCompetitor.value) {
            return;
        }

        copyingInProgress.value = true;

        return eventCompetitorsApi
            .copyToCategory(eventId.value, registeredCompetitor.value.id, selectedCategory.value.key)
            .then(response => {
                close();

                if (response.sideEffects.length === 0) {
                    successToast(translate("admin_competitors.competitor_copied"));
                } else {
                    warningToast(translate("side_effects_modal.event_registration.change_caused_side_effects"), 5000);
                    eventBus.$emit(OPEN_SIDE_EFFECTS_REPORT_MODAL, {
                        sideEffects: response.sideEffects,
                        eventId: eventId.value,
                        registeredCompetitor: registeredCompetitor.value!,
                    } satisfies EventRegistrationSideEffectReport);
                }

                return store.getRegisteredCompetitorsPage();
            })
            .catch(handleError)
            .finally(() => {
                copyingInProgress.value = false;
            });
    }

    function close() {
        modal.value.close();
    }
</script>
